import { Button, Skeleton } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { useDateFormatter } from "../../../../hooks/use-date-formatter"
import {
  homeCardStyle,
  homeColor,
  homePastel,
  homeText
} from "../../home-theme.ts"
import { ClipboardCheckIcon } from "../home-icons.tsx"
import { type ApprovalKind, useHomeApprovals } from "./useHomeApprovals"

const KIND_LABEL: Record<ApprovalKind, { key: string; fallback: string }> = {
  proposal: {
    key: "home.approvals.kind.proposal",
    fallback: "Proposal approval"
  },
  contract: {
    key: "home.approvals.kind.contract",
    fallback: "Contract approval"
  },
  review: { key: "home.approvals.kind.review", fallback: "Document review" },
  signature: {
    key: "home.approvals.kind.signature",
    fallback: "Signature request"
  }
}

const KIND_ACTION: Record<ApprovalKind, { key: string; fallback: string }> = {
  proposal: {
    key: "home.approvals.action.proposal",
    fallback: "Review proposal"
  },
  contract: {
    key: "home.approvals.action.contract",
    fallback: "Review contract"
  },
  review: { key: "home.approvals.action.review", fallback: "Review document" },
  signature: {
    key: "home.approvals.action.signature",
    fallback: "Open request"
  }
}

const KIND_TINT: Record<ApprovalKind, { bg: string; fg: string }> = {
  proposal: homePastel.sky,
  contract: homePastel.lilac,
  review: homePastel.mint,
  signature: homePastel.peach
}

/**
 * §7's Approvals & signatures card, over the real queues.
 *
 * One row per thing waiting on the user — proposal, contract, document
 * review, signature request — oldest first, three shown, View all to the
 * Approval pages. "All caught up" is said only once every permitted source
 * answered with nothing; a source that could not be read makes the card say
 * so and offer a retry rather than pretend the queue is clear.
 */
export const HomeApprovalsCard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { formatDate } = useDateFormatter()
  const { rows, total, state, retry } = useHomeApprovals()

  return (
    <section
      style={{ ...homeCardStyle, overflow: "hidden", minWidth: 0 }}
      data-testid="home-approvals-card"
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 16px",
          borderBottom: `1px solid ${homeColor.border}`
        }}
      >
        <span
          aria-hidden
          style={{
            width: 28,
            height: 28,
            borderRadius: 7,
            background: homeColor.tealPale,
            color: homeColor.teal,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <ClipboardCheckIcon width={14} height={14} />
        </span>
        <h3
          style={{
            ...homeText.itemTitle,
            color: homeColor.ink,
            margin: 0,
            flex: 1,
            textAlign: "start"
          }}
        >
          {t("home.schedule.approvals", "Approvals & signatures")}
        </h3>
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          onClick={() => navigate("/approval")}
        >
          {total > rows.length
            ? t("home.approvals.viewAllCount", "View all ({{count}})", {
                count: total
              })
            : t("common.viewAll", "View all")}
        </Button>
      </div>

      {state === "loading" ? (
        <div style={{ padding: 16 }} data-testid="home-approvals-loading">
          <Skeleton active paragraph={{ rows: 3 }} title={false} />
        </div>
      ) : state === "error" ? (
        <div style={{ padding: "20px 16px", textAlign: "center" }} role="alert">
          <div style={{ ...homeText.body, color: homeColor.inkMuted }}>
            {t("home.approvals.error", "Approvals could not be loaded.")}
          </div>
          <Button type="link" onClick={retry}>
            {t("common.retry", "Retry")}
          </Button>
        </div>
      ) : state === "empty" ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 6,
            padding: "28px 16px",
            textAlign: "center"
          }}
          data-testid="home-approvals-empty"
        >
          <span
            aria-hidden
            style={{
              width: 44,
              height: 44,
              borderRadius: 12,
              background: homeColor.chipBg,
              color: homeColor.inkMuted,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <ClipboardCheckIcon width={22} height={22} />
          </span>
          <span style={{ ...homeText.itemTitle, color: homeColor.ink }}>
            {t("home.approvals.allCaughtUp", "You're all caught up")}
          </span>
          <span style={{ ...homeText.meta, color: homeColor.inkMuted }}>
            {t(
              "home.approvals.noPending",
              "No pending approvals or signature requests"
            )}
          </span>
        </div>
      ) : (
        rows.map((row) => {
          const tint = KIND_TINT[row.kind]
          return (
            <div
              key={row.id}
              className="home-schedule__row"
              data-testid="home-approval-row"
            >
              <span
                aria-hidden
                className="home-schedule__date"
                style={{ background: tint.bg, color: tint.fg }}
              >
                <ClipboardCheckIcon width={18} height={18} />
              </span>
              <span
                className="min-w-0"
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 2
                }}
              >
                <span
                  style={{
                    ...homeText.chip,
                    color: tint.fg,
                    letterSpacing: "0.04em"
                  }}
                >
                  {t(
                    KIND_LABEL[row.kind].key,
                    KIND_LABEL[row.kind].fallback
                  ).toUpperCase()}
                </span>
                <span
                  style={{ ...homeText.actionTitle, color: homeColor.ink }}
                  className="truncate"
                >
                  {row.entity}
                </span>
                <span
                  style={{ ...homeText.meta, color: homeColor.inkMuted }}
                  className="truncate"
                >
                  {[row.context, row.since ? formatDate(row.since) : null]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <Button
                size="small"
                onClick={() => navigate(row.to)}
                style={{ borderRadius: 8, flexShrink: 0 }}
              >
                {t(KIND_ACTION[row.kind].key, KIND_ACTION[row.kind].fallback)}
              </Button>
            </div>
          )
        })
      )}
    </section>
  )
}
